import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { Button, Drawer, Tag } from "@stonelog/stonelog-design-system";
import machineError from "@assets/imgs/machine-error.svg";
import { toCapitalizeFirstLetterOfEachWord } from "../../../../consumingApi/services/helper";
import MiniMap from "./MiniMap";
import { getEtapaLabel } from "./etapas";
import ImageGallery from "@components/ImageGallery";
import {
    getAttachmentStoneEntregaDetails,
    attachmentSelector,
    resetAttachmentStoneEntregaDetails
} from "../../../../features/anexos/attachmentSlice";

import * as S from "./styles";

const INLINE_STYLES = {
    headerCursor: { cursor: "default" }
};

const AttachmentStoneEntregaDrawer = ({ open, setOpen, rowClickedInfos }) => {
    const dispatch = useDispatch();
    const [openItems, setOpenItems] = useState({});

    const {
        getAttachmentStoneEntregaDetailsData,
        getAttachmentStoneEntregaDetailsStatus
    } = useSelector(attachmentSelector);

    const listData = getAttachmentStoneEntregaDetailsData?.data || getAttachmentStoneEntregaDetailsData || [];
    const hasData = listData.length > 0;

    const sortedListData = useMemo(() => {
        if (!hasData) return [];
        return [...listData].sort((a, b) => {
            const isAColeta = a.etapa?.toUpperCase() === "COLETA";
            const isBColeta = b.etapa?.toUpperCase() === "COLETA";
            if (isAColeta && !isBColeta) return -1;
            if (!isAColeta && isBColeta) return 1;
            return 0;
        });
    }, [listData, hasData]);

    useEffect(() => {
        if (open && rowClickedInfos?.tracking_code) {
            dispatch(getAttachmentStoneEntregaDetails(rowClickedInfos.tracking_code));
        }
    }, [open, rowClickedInfos, dispatch]);

    const onClose = () => {
        setOpen(false);
        dispatch(resetAttachmentStoneEntregaDetails());
        setOpenItems({});
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        return dayjs(dateStr).format('DD/MM/YYYY');
    };

    const toggleItem = (index) => {
        setOpenItems((prev) => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const renderContent = () => {
        if (getAttachmentStoneEntregaDetailsStatus === "pending") return <Drawer.Loading />;

        if (getAttachmentStoneEntregaDetailsStatus === "rejected") {
            return <Drawer.Response element={<img src={machineError} alt="Erro" />} message="Erro ao buscar dados." />;
        }

        if (!hasData) {
            return <Drawer.Response element={<img src={machineError} alt="Sem dados" />} message="Nenhum detalhe encontrado." />;
        }

        return (
            <S.AllBodyContainer>
                {sortedListData.map((evidencia, evIndex) => {
                    const isColeta = evidencia.etapa?.toUpperCase() === "COLETA";
                    // Está aberto se for Coleta OU se o usuário clicou para abrir
                    const isOpen = isColeta || !!openItems[evIndex];

                    const trackingCode = rowClickedInfos?.tracking_code || "anexo";
                    const etapaSlug = (evidencia.etapa || `etapa-${evIndex + 1}`)
                        .toString()
                        .toLowerCase()
                        .replace(/\s+/g, '-');

                    return (
                        <S.BodyMiddleContainer key={evIndex}>
                            <div
                                onClick={() => !isColeta && toggleItem(evIndex)}
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    cursor: isColeta ? "default" : "pointer",
                                    paddingBottom: isOpen ? "8px" : "0"
                                }}
                            >
                                <S.BodyTitleTag>
                                    <p style={{ margin: 0, marginRight: "8px", fontSize: "16px" }}>
                                        Etapa do atendimento
                                    </p>
                                    <Tag text={getEtapaLabel(evidencia.etapa)} />
                                </S.BodyTitleTag>

                                {!isColeta && (
                                    <svg
                                        width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6A7181" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                        style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s ease" }}
                                    >
                                        <polyline points="6 9 12 15 18 9"></polyline>
                                    </svg>
                                )}
                            </div>

                            {isOpen && (
                                <div style={{ animation: "fadeIn 0.3s ease-in-out" }}>
                                    <S.BodyTopContainer>
                                        <S.BodyTopContent>
                                            <p>Grau de parentesco (Recebedor)</p>
                                            <span>{toCapitalizeFirstLetterOfEachWord(evidencia.recebedor) || "-"}</span>
                                        </S.BodyTopContent>

                                        <S.BodyTopContent>
                                            <p>Nome de quem recebeu</p>
                                            <span>{toCapitalizeFirstLetterOfEachWord(evidencia.nome_completo) || "-"}</span>
                                        </S.BodyTopContent>

                                        <S.BodyTopContent>
                                            <p>Documento informado</p>
                                            <span>{evidencia.num_documento || "-"}</span>
                                        </S.BodyTopContent>

                                        {evidencia.num_locker && (
                                            <S.BodyTopContent>
                                                <p>Número do locker</p>
                                                <span>{evidencia.num_locker}</span>
                                            </S.BodyTopContent>
                                        )}

                                        <S.BodyTopContent>
                                            <p>Angel responsável</p>
                                            <span>{evidencia.angel_responsavel || "-"}</span>
                                        </S.BodyTopContent>

                                        <S.BodyTopContent>
                                            <p>Data de atendimento</p>
                                            <span>{formatDate(evidencia.data_atendimento)}</span>
                                        </S.BodyTopContent>

                                        {/* <S.BodyTopContent> REMOVIDO PARA ANALISE
                                            <p>Status</p>
                                            <span>{evidencia.status || "-"}</span>
                                        </S.BodyTopContent> */}
                                    </S.BodyTopContainer>

                                    <S.BodyMiddleHeader style={{ cursor: 'default' }}>
                                        <p style={{ fontSize: "16px", color: "#6A7181" }}>
                                            Anexos do Atendimento
                                        </p>
                                    </S.BodyMiddleHeader>

                                    <ImageGallery
                                        images={evidencia.fotos || []}
                                        fileName={`${trackingCode}-${etapaSlug}`}
                                    />

                                    <S.BodyMiddleHeader style={{ cursor: 'default' }}>
                                        <p style={{ fontSize: "16px", color: "#6A7181" }}>
                                            Localização do Atendimento
                                        </p>
                                    </S.BodyMiddleHeader>

                                    <MiniMap evidencia={evidencia} />
                                </div>
                            )}
                        </S.BodyMiddleContainer>
                    );
                })}
            </S.AllBodyContainer>
        );
    };

    return (
        <Drawer.Root
            placement="right"
            open={open}
            onClose={onClose}
            mask={true}
            title={
                <Drawer.Header
                    title={`Pedido ${rowClickedInfos?.tracking_code || ""}`}
                    date={new Date()}
                />
            }
        >
            <Drawer.Body>{renderContent()}</Drawer.Body>
            <Drawer.Footer>
                <Button width="100%" height="52px" size="large" onClick={onClose}>Fechar</Button>
            </Drawer.Footer>
        </Drawer.Root>
    );
};

export default AttachmentStoneEntregaDrawer;
